import {Component, inject, input, InputSignal} from '@angular/core';
import {environment} from '@environments/environment';
import {OidcToken} from '../../auth/oidc/oidc-token';
import {SupportInformationService} from '../../core/support/support-information.service';
import {UserData} from '../../core/user/models/user-data';
import {UserService} from '../../core/user/services/user.service';
import {AwpClientLibService} from '../../services/awp-client-lib.service';
import {DsBoxModule, DsButtonModule, DsListItemModule} from '@bmw-ds/components';
import {UserSettingsOverlayComponent} from './user-settings-overlay.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-user-dropdown',
  templateUrl: './user-dropdown.component.html',
  styleUrls: ['./user-dropdown.component.scss'],
  imports: [DsButtonModule, DsBoxModule, DsListItemModule, UserSettingsOverlayComponent, TranslateModule]
})
export class UserDropdownComponent {
  private readonly userService: UserService = inject(UserService);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);
  supportInformationService: SupportInformationService = inject(SupportInformationService);

  readonly viewState: InputSignal<{
    userDropdownVisible: boolean,
    settingsDialogVisible: boolean
  }>
    = input<{
    userDropdownVisible: boolean,
    settingsDialogVisible: boolean
  }>();

  user: UserData;

  constructor() {
    this.userService.user$.subscribe((userData: UserData) => this.user = userData);
  }

  toggleUserDropdown(): void {
    this.viewState().userDropdownVisible = !this.viewState().userDropdownVisible;
  }

  closeUserDropdown(): void {
    this.viewState().userDropdownVisible = false;
  }

  openSettings(): void {
    this.closeUserDropdown();
    this.viewState().settingsDialogVisible = true;
  }

  logOut(): void {
    this.userService.logOut();
  }

  copySupportInformation(): void {
    this.copyToClipboard(
      this.supportInformationService.collectSupportInformation(this.user),
      'Copied support information.'
    );
    this.closeUserDropdown();
  }

  copyAccessToken(): void {
    const oidcToken: OidcToken = OidcToken.readFromStorage(this.userService.getCurrentUser().getContext());
    this.copyToClipboard(oidcToken.access_token, 'Access token copied to clipboard.');
    this.closeUserDropdown();
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  copyAccessTokenVisible(): boolean {
    return !['PROD', 'PROD_US', 'PROD_CN'].includes(environment.deploymentEnvironment);
  }

  private copyToClipboard(text: string, successMessage: string): void {
    navigator.clipboard
      .writeText(text)
      .then((): void => this.awpClientLibService.serverApi.notifySuccess(successMessage))
      .catch((): void => this.copyToClipboardForThinClientAndOlderBrowsers(text, successMessage));
  }

  private copyToClipboardForThinClientAndOlderBrowsers(text: string, successMessage: string): void {
    const element: HTMLTextAreaElement = document.createElement('textarea');
    document.body.appendChild(element);
    element.value = text;
    element.select();
    document.execCommand('copy', false);
    element.remove();
    this.awpClientLibService.serverApi?.notifySuccess(successMessage);
  }
}
